import React, { useState, useEffect } from 'react';
import { Modal, Box, Button } from '@mui/material';

import type { ExamDTO } from '../models/examDTOs';
import { apiRequest } from '@/src/services';
import { Loading } from '@/src/ui';

interface DeleteModalProps {
    resourceId: number | undefined;
    handleClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ resourceId, handleClose }) => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [firstConfirm, setFirstConfirm] = useState(false);

    const [data, setData] = useState<ExamDTO | null>(null);

    useEffect(() => {
        (async function init() {
            let res = null;
            try {
                res = await apiRequest<ExamDTO>('GET', `assessments/exams/noXml/${resourceId}`);
            } catch {
                setError('Failed to load exam data');
                return;
            } finally {
                setHasLoaded(true);
            }

            setData(res.data);
        })();
    }, [resourceId]);

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();

        if (submitting || success) return;

        if (!data?.id) setError('Unable to load exam Id');

        if (!firstConfirm) {
            setFirstConfirm(true);
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            await apiRequest<number>('DELETE', `assessments/exams/${data?.id}`);
            setSuccess(true);
            setTimeout(() => {
                handleClose(true, false);
                setFirstConfirm(false);
                setSuccess(false);
            }, 750);
        } catch {
            setError('Failed to delete exam');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={true} onClose={() => handleClose(false, success)}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>Delete Exam</h2>
                {!hasLoaded ? (
                    <Loading message="Loading..." size="lg" />
                ) : data ? (
                    <>
                        <p>Are you sure you want to delete this exam?</p>
                        <p>
                            <strong>Name:</strong> {data.title}
                        </p>
                    </>
                ) : (
                    <p>No exam selected.</p>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    {!firstConfirm ? (
                        <>
                            <Button variant="outlined" onClick={() => handleClose(false, false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleSubmit}>
                                Delete
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                disabled={submitting || success}
                                variant="contained"
                                color="error"
                                onClick={handleSubmit}
                            >
                                Confirm Delete
                            </Button>
                            <Button
                                disabled={submitting || success}
                                variant="outlined"
                                onClick={() => handleClose(false, false)}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
                {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                {success && (
                    <div style={{ color: 'green', marginTop: 8 }}>Exam deleted successfully!</div>
                )}
                {submitting && <Loading message="Processing..." size="sm" />}
            </Box>
        </Modal>
    );
};

export default DeleteModal;
